// Topic-based question bank for the General knowledge check track.
// The agent can run a "mixed" set (sampled across all topics) or pick one topic.
// Supported question types match src/data/questions.js:
//   multiple_choice, true_false, scenario, sequence, compliance, ticket_simulation.

export const GENERAL_TOPICS = [
  {
    key: "billing",
    label: "Billing process",
    icon: "CreditCard",
    desc: "Subscriptions, proration, billing cycles, and invoice adjustments."
  },
  {
    key: "refund_policy",
    label: "Refund policy",
    icon: "RotateCcw",
    desc: "Refund eligibility, windows, partial refunds, and policy limits."
  },
  {
    key: "legal",
    label: "Legal & compliance",
    icon: "Gavel",
    desc: "GDPR/Data requests, withdrawal rights, and what we can/can't promise."
  },
  {
    key: "internal_systems",
    label: "Internal systems",
    icon: "Database",
    desc: "Chargebee, Stripe, Backoffice, Reteno — where to look and what to trust."
  },
  {
    key: "cc_flows",
    label: "CC flows",
    icon: "ArrowLeftRight",
    desc: "Chargebacks, representment evidence, and when to escalate to the CC team."
  },
  {
    key: "company_products",
    label: "Company products",
    icon: "Package",
    desc: "Plans, features, trials, and what each product tier includes."
  },
  {
    key: "tech_issues",
    label: "Tech issues",
    icon: "Wrench",
    desc: "App crashes, login failures, caching, and standard troubleshooting steps."
  },
  {
    key: "retention",
    label: "Retention tactics",
    icon: "HeartHandshake",
    desc: "Save offers, win-back timing, and turning cancellations into renewals."
  }
];

export const TOPIC_QUESTIONS = {
  billing: [
    {
      id: "billing-1",
      type: "multiple_choice",
      question:
        "A customer wants to switch from monthly to annual billing mid-cycle. What's the correct approach?",
      options: [
        "Prorate the difference: credit the unused monthly balance and charge the annual plan from today.",
        "Cancel the current subscription and start a fresh annual one next month.",
        "Refund the full month and charge the annual plan from the original renewal date.",
        "Tell the customer they must wait until the cycle ends to change plans."
      ],
      correctIndex: 0,
      explanation:
        "Mid-cycle plan changes use proration — credit the unused portion of the old plan and start the new one today, so the customer isn't double-charged."
    },
    {
      id: "billing-2",
      type: "sequence",
      question:
        "Order the steps to handle a customer who was charged the wrong amount.",
      tiles: [
        "Confirm the expected vs. actual charge amount and the plan they intended.",
        "Check the subscription and invoice in Chargebee/Stripe to see what was billed.",
        "Identify the cause (proration error, failed upgrade, duplicate charge).",
        "Apply the correction: adjust the invoice, issue a credit, or refund the difference.",
        "Confirm the corrected amount and next billing date with the customer."
      ],
      correctOrder: [0, 1, 2, 3, 4],
      explanation:
        "Verify what happened in the system before changing anything, fix the root cause, then confirm the corrected state with the customer."
    },
    {
      id: "billing-3",
      type: "ticket_simulation",
      topic: "Charged the wrong amount",
      customer: "Alex P.",
      knowledgeBase:
        "Billing: confirm expected vs. actual amount in Chargebee/Stripe, identify the cause (proration, failed upgrade, duplicate), apply a credit or refund for the difference, and confirm the corrected amount and next billing date with the customer.",
      turns: [
        {
          messages: {
            friendly: "Hi! I think I was charged €29 instead of €19 this month?",
            neutral: "Hi, I was charged €29 but my plan is €19. Can you check?",
            aggressive: "You charged me the wrong amount! €29 instead of €19 — fix it now."
          },
          options: [
            "Hi Alex! Let me pull up your invoice and see exactly what was charged — one moment.",
            "Our system doesn't make billing mistakes.",
            "Just refund it yourself from your bank app."
          ],
          correctIndex: 0
        },
        {
          messages: {
            friendly: "Sure, take your time.",
            neutral: "Okay, waiting.",
            aggressive: "I don't have all day, just fix it."
          },
          options: [
            "I see the charge — it looks like a proration from your plan change last week. Let me verify and correct it.",
            "I can't tell why it happened, I'll just refund everything.",
            "That's normal, proration always adds €10."
          ],
          correctIndex: 0
        },
        {
          messages: {
            friendly: "Ah okay, what happens now?",
            neutral: "And the fix?",
            aggressive: "So are you refunding me or not?"
          },
          options: [
            "I've applied a €10 credit to your account and confirmed your next invoice will be €19. You'll see it on your next bill.",
            "I refunded the whole subscription, you'll need to resubscribe.",
            "Nothing I can do, proration is final."
          ],
          correctIndex: 0
        }
      ],
      stressTurns: [
        {
          kind: "out_of_scope",
          message:
            "Also, can you change my billing currency to USD and backdate it to last year for tax reasons?",
          options: [
            "Changing currency and backdating billing isn't something I can do — that would need a finance review. I'll flag it with the right team and follow up.",
            "Sure, I'll backdate everything right now.",
            "We don't do that, sorry."
          ],
          correctIndex: 0
        },
        {
          kind: "mind_change",
          message:
            "Wait, actually don't credit it — just refund the whole charge. No wait, keep the credit. Ugh, whatever's simplest.",
          options: [
            "No problem, Alex — the credit is already applied and is the simplest option, so let's keep it. Your next bill will be €19. Sound good?",
            "I already refunded everything, can't undo it.",
            "Make up your mind and get back to me."
          ],
          correctIndex: 0
        }
      ]
    }
  ],

  refund_policy: [
    {
      id: "refund-1",
      type: "multiple_choice",
      question:
        "A customer requests a refund 40 days after purchase. The policy window is 28 days. What's the right response?",
      options: [
        "Explain the 28-day window has passed, review if any exception applies, and offer next steps if not.",
        "Refund them anyway — policies are just guidelines.",
        "Ignore the request since it's outside the window.",
        "Tell them refunds are never possible."
      ],
      correctIndex: 0,
      explanation:
        "State the policy clearly, check for any documented exception (e.g., service-impacting bug), and give a clear next step. Never promise a refund outside policy without authorization."
    },
    {
      id: "refund-2",
      type: "true_false",
      question:
        "Partial refunds are allowed when only part of the service period was used and the request is within the refund window.",
      correctAnswer: true,
      explanation:
        "Within the window, partial refunds reflect actual usage and are standard practice — verify usage in the system before issuing."
    },
    {
      id: "refund-3",
      type: "compliance",
      question:
        "Audit this draft reply and highlight the two policy violations.",
      segments: [
        { text: "Hi! I checked your account. Since you're past the 28-day window, I " },
        { text: "can't refund you, but I'll do it this one time as a favour", error: true, id: "favour" },
        { text: ". Also, I can " },
        { text: "guarantee you'll never be charged again", error: true, id: "guarantee" },
        { text: " if you stay on this plan." }
      ],
      explanation:
        "Promising a one-off refund outside policy without authorization is a violation, and guaranteeing 'never be charged again' is a promise we can't make — billing depends on the active subscription."
    }
  ],

  legal: [
    {
      id: "legal-1",
      type: "multiple_choice",
      question:
        "A customer submits a GDPR data deletion request. What's the correct first step?",
      options: [
        "Verify the user's identity, then route the request to the privacy/legal team using the documented deletion process.",
        "Delete their account immediately from the support console.",
        "Tell them we don't store personal data.",
        "Ask them to put the request in writing via postal mail only."
      ],
      correctIndex: 0,
      explanation:
        "Verify identity, then follow the documented GDPR deletion workflow — support doesn't delete data ad hoc; it's routed to the team that handles it within the legal timeframe."
    },
    {
      id: "legal-2",
      type: "true_false",
      question:
        "Customers in the EU have a 14-day right of withdrawal from their subscription contract.",
      correctAnswer: false,
      explanation:
        "The withdrawal window is 28 days, not 14. Understating it is a compliance violation."
    },
    {
      id: "legal-3",
      type: "compliance",
      question:
        "Audit this draft reply and highlight the two legal/compliance violations.",
      segments: [
        { text: "Hello! Per your data request, I'll " },
        { text: "delete everything including billing records right now", error: true, id: "billing" },
        { text: ". And regarding your refund, you have a " },
        { text: "14-day money-back guarantee by law", error: true, id: "window" },
        { text: " so I'll process that too." }
      ],
      explanation:
        "Billing/transaction records must be retained for legal/tax reasons and can't be deleted on request — only personal data is removed. The withdrawal window is 28 days, not 14."
    }
  ],

  internal_systems: [
    {
      id: "systems-1",
      type: "multiple_choice",
      question:
        "A customer's subscription shows active in Chargebee but they have no access. Where do you check first?",
      options: [
        "Verify the entitlement/access flag in the Backoffice, then reconcile with Chargebee status.",
        "Assume Chargebee is wrong and manually grant access.",
        "Ask the customer to reinstall the app.",
        "Check Reteno for their push notification history."
      ],
      correctIndex: 0,
      explanation:
        "Chargebee holds billing/subscription state; the Backoffice holds entitlements. Reconcile the two before changing anything — a sync delay is the most common cause."
    },
    {
      id: "systems-2",
      type: "true_false",
      question:
        "Reteno is used for lifecycle messaging and push campaigns, not as the source of truth for subscription billing status.",
      correctAnswer: true,
      explanation:
        "Reteno is the engagement/messaging layer; billing truth lives in Chargebee/Stripe. Don't trust Reteno for billing decisions."
    },
    {
      id: "systems-3",
      type: "sequence",
      question:
        "Order the steps to locate a charge the customer can see but support can't initially find.",
      tiles: [
        "Ask the customer for the exact date, amount, and payment method of the charge.",
        "Search Stripe by the amount/date and the customer's email.",
        "Cross-reference Chargebee invoices for the same period.",
        "Check the Backoffice for entitlement/sync status if found in one but not the other.",
        "Report findings to the customer with evidence (invoice/charge ID)."
      ],
      correctOrder: [0, 1, 2, 3, 4],
      explanation:
        "Get precise details from the customer, search the gateway, reconcile with the billing system, check entitlements, then respond with evidence."
    }
  ],

  cc_flows: [
    {
      id: "cc-1",
      type: "multiple_choice",
      question:
        "A customer files a chargeback claiming they never authorized the subscription, but logs show they used the product daily. What's the right move?",
      options: [
        "Gather usage evidence and submit representment through the CC team to dispute the chargeback.",
        "Immediately refund the customer to avoid the chargeback fee.",
        "Ignore the chargeback — it will resolve itself.",
        "Cancel the customer's account and block them."
      ],
      correctIndex: 0,
      explanation:
        "With clear usage evidence, this is 'friendly fraud' — submit representment via the CC team with proof of authorized use. Don't auto-refund valid chargebacks."
    },
    {
      id: "cc-2",
      type: "true_false",
      question:
        "Representment evidence can include login history, usage logs, and the original payment authorization.",
      correctAnswer: true,
      explanation:
        "Strong representment evidence shows the customer authorized and used the service: login/usage logs plus the original authorization record."
    },
    {
      id: "cc-3",
      type: "scenario",
      question:
        "A customer opens a chargeback for a charge they don't recognize, but you find it's a legitimate renewal they forgot about. Describe how you'd handle it.",
      referenceAnswer:
        "1) Pull the original payment authorization and renewal notification history. 2) Confirm the charge matches an active, used subscription. 3) Contact the customer to explain the charge with evidence. 4) If they still dispute, submit representment via the CC team with usage and authorization proof. 5) Log the case for QA.",
      explanation:
        "Investigate with evidence, communicate with the customer first, and only escalate to representment when the charge is clearly legitimate and the dispute continues."
    }
  ],

  company_products: [
    {
      id: "products-1",
      type: "multiple_choice",
      question:
        "A customer asks whether the Pro plan includes advanced analytics. You're not 100% sure. What do you do?",
      options: [
        "Check the product/plan matrix in the knowledge base, and confirm before answering.",
        "Say yes to keep the customer happy.",
        "Tell them to check the website themselves.",
        "Guess based on the plan name."
      ],
      correctIndex: 0,
      explanation:
        "Never guess about plan features — verify in the documented plan matrix and answer with confidence."
    },
    {
      id: "products-2",
      type: "true_false",
      question:
        "A free trial requires payment details upfront but doesn't charge until the trial ends, and the customer can cancel anytime before then.",
      correctAnswer: true,
      explanation:
        "Standard trial flow: payment method on file, no charge until the trial period ends, cancellation prevents the first charge."
    },
    {
      id: "products-3",
      type: "ticket_simulation",
      topic: "Which plan is right for me?",
      customer: "Sam K.",
      knowledgeBase:
        "Plans: Starter (1 seat, basic analytics), Pro (5 seats, advanced analytics + automations), Business (unlimited seats, SSO, priority support). Trials are 7 days, no charge until trial ends, cancel anytime.",
      turns: [
        {
          messages: {
            friendly: "Hi! I'm not sure whether I need Pro or Business — can you help?",
            neutral: "Hi, can you explain the difference between Pro and Business?",
            aggressive: "Your pricing page is confusing — just tell me which plan I need!"
          },
          options: [
            "Hi Sam! Happy to help — tell me a bit about your team size and what you need (analytics, automations, SSO?) and I'll match you to the right plan.",
            "Just buy Business, it's the best one.",
            "I can't help you choose, read the pricing page."
          ],
          correctIndex: 0
        },
        {
          messages: {
            friendly: "We're a team of 4 and want advanced analytics, maybe automations.",
            neutral: "Team of 4, need advanced analytics and automations.",
            aggressive: "We're 4 people and need analytics, stop asking questions."
          },
          options: [
            "Sounds like Pro is a great fit — it covers 5 seats, advanced analytics, and automations. You can upgrade to Business later if you need SSO or more seats.",
            "You need Business for analytics.",
            "Starter is enough for 4 people."
          ],
          correctIndex: 0
        },
        {
          messages: {
            friendly: "Great, and the trial is free for 7 days?",
            neutral: "Confirm the trial terms?",
            aggressive: "Just confirm I won't be charged for the trial, I've heard that before."
          },
          options: [
            "Yes — the 7-day trial is free, we don't charge until it ends, and you can cancel anytime before then.",
            "You'll be charged €1 to verify your card.",
            "The trial charges you after 3 days."
          ],
          correctIndex: 0
        }
      ],
      stressTurns: [
        {
          kind: "out_of_scope",
          message:
            "Also, can you build a custom plan between Pro and Business just for us with a special price?",
          options: [
            "Custom plans aren't something I can create from support — I'll connect you with our sales team who can discuss that.",
            "Sure, I'll make a custom plan for you right now.",
            "No, take one of the existing plans."
          ],
          correctIndex: 0
        },
        {
          kind: "mind_change",
          message:
            "Actually, maybe Business is better. No wait, Pro. Hmm, can you just put me on the highest plan and discount it?",
          options: [
            "No problem, Sam — let's go with Pro since it fits your 4-person team and needs. If you later want SSO or more seats, upgrading to Business is one click. Want me to start the Pro trial?",
            "I already put you on Business, can't change it.",
            "I can't discount anything, pick one."
          ],
          correctIndex: 0
        }
      ]
    }
  ],

  tech_issues: [
    {
      id: "tech-1",
      type: "multiple_choice",
      question:
        "A customer reports the app crashes on startup. What's the best first step?",
      options: [
        "Ask for the device, OS version, and app version, then check known issues and suggest a clean reinstall.",
        "Tell them it's probably their phone and close the ticket.",
        "Immediately escalate to engineering without any details.",
        "Ask them to factory reset their device."
      ],
      correctIndex: 0,
      explanation:
        "Gather environment details first, check for known issues, and try a clean reinstall — most startup crashes are resolved by reinstall or a known-issue workaround."
    },
    {
      id: "tech-2",
      type: "sequence",
      question:
        "Order the troubleshooting steps for a customer who can't log in.",
      tiles: [
        "Confirm the account exists and the email is correct.",
        "Ask for device and browser, and whether the password reset link was tried.",
        "Send/trigger a password reset and confirm they receive the email.",
        "Check for failed login attempts or account locks in the Backoffice.",
        "Escalate to engineering if all steps fail and the account is valid."
      ],
      correctOrder: [0, 1, 2, 3, 4],
      explanation:
        "Confirm the account, gather environment info, try a reset, check for locks, then escalate — don't escalate before exhausting the standard steps."
    },
    {
      id: "tech-3",
      type: "true_false",
      question:
        "Asking the customer to clear cache/cookies or try an incognito window is a valid early step for login and loading issues.",
      correctAnswer: true,
      explanation:
        "Stale cache/cookies commonly cause login and loading failures; an incognito window quickly isolates the cause before deeper investigation."
    }
  ],

  retention: [
    {
      id: "retention-1",
      type: "multiple_choice",
      question:
        "A customer writes in to cancel. What's the best retention approach?",
      options: [
        "Acknowledge their reason, offer a relevant save option (pause, discount, or plan change), and make cancellation easy if they still want it.",
        "Make cancellation as hard as possible to keep them.",
        "Cancel immediately without asking why.",
        "Offer a huge discount regardless of their reason."
      ],
      correctIndex: 0,
      explanation:
        "Understand the reason, offer a relevant save, but never block cancellation — friction destroys trust and increases chargebacks."
    },
    {
      id: "retention-2",
      type: "scenario",
      question:
        "A long-term customer wants to cancel due to budget. Describe your retention approach.",
      referenceAnswer:
        "1) Acknowledge their loyalty and the budget concern. 2) Offer options: pause the subscription for 1-3 months, downgrade to a lower plan, or apply a temporary loyalty discount. 3) Explain each option's impact. 4) If they still want to cancel, process it cleanly and set a win-back touchpoint. 5) Log the reason and offer for QA.",
      explanation:
        "Match the save offer to the stated reason (budget → pause/downgrade/discount), keep cancellation frictionless, and plan a win-back."
    },
    {
      id: "retention-3",
      type: "true_false",
      question:
        "Win-back outreach is most effective shortly after cancellation while the customer still remembers the product's value.",
      correctAnswer: true,
      explanation:
        "Early win-back (within days to a couple weeks) outperforms long-delayed outreach because the product is still top of mind."
    }
  ]
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Build the question set for a general-track run.
// "mixed" samples across all topics (round-robin, then shuffled, capped) for variety;
// any other key returns that topic's full question set.
export function buildGeneralSet(topicKey) {
  if (topicKey && topicKey !== "mixed") {
    return TOPIC_QUESTIONS[topicKey] ? [...TOPIC_QUESTIONS[topicKey]] : [];
  }

  // Mixed: take up to 2 from each topic round-robin, then shuffle and cap at 12.
  const perTopic = 2;
  const sampled = [];
  for (let i = 0; i < perTopic; i++) {
    for (const t of GENERAL_TOPICS) {
      const qs = TOPIC_QUESTIONS[t.key] || [];
      if (qs[i]) sampled.push(qs[i]);
    }
  }
  return shuffle(sampled).slice(0, 12);
}

export function getTopicCount(topicKey) {
  if (topicKey === "mixed") return buildGeneralSet("mixed").length;
  return (TOPIC_QUESTIONS[topicKey] || []).length;
}